/**
 * Фигура: прямоугольник, эллипс, линия, стрелка или штрих от руки.
 *
 * Самый простой примитив, и нужен он не ради красоты: подложка
 * под текст, рамка вокруг вебки, плашка под алерт. Всё это делают
 * фигурой, потому что иначе пришлось бы грузить картинку ради
 * заливки одним цветом.
 *
 * ДВА СПОСОБА РИСОВАНИЯ ПОД ОДНИМ ТИПОМ, и это не экономия на
 * типах. Прямоугольник и эллипс — это заливка коробки слоя, и div
 * с background рисует их дешевле всего. Линия, стрелка и штрих —
 * это ЛОМАНАЯ внутри коробки, и коробкой её не выразить вовсе.
 * Общее у них то, ради чего тип и заводят: цвет, толщина обводки,
 * непрозрачность и место на сцене.
 *
 * КООРДИНАТЫ ХРАНЯТСЯ В СОБСТВЕННЫХ ЕДИНИЦАХ РИСУНКА, а не
 * в долях коробки. Так их и рисуют: точка приходит от мыши
 * в единицах полотна, и приводить её к долям пришлось бы дважды —
 * при рисовании и обратно при показе. Взамен viewBox считается
 * от самих точек, и коробка слоя оказывается ровно рамкой
 * нарисованного.
 */
import { asRecord, color, numberInRange, oneOf, pointList } from './props.js';
const TYPE = 'shape';
const KINDS = ['rect', 'ellipse', 'line', 'arrow', 'stroke'];
/** Виды, которые рисуются ломаной, а не заливкой коробки. */
const DRAWN = new Set(['line', 'arrow', 'stroke']);
/**
 * Потолок на число точек в штрихе.
 *
 * ЗАЧЕМ ОН ЕСТЬ. Точки приходят из сети и уходят прямо в DOM
 * оверлея, который работает без присмотра во время эфира: список
 * без потолка — это чужой узел, растущий, пока хватает памяти.
 *
 * ПОЧЕМУ ИМЕННО СТОЛЬКО. Число подбиралось дважды, и первый раз —
 * слишком жадно. Была тысяча, и её хватало не всякому штриху:
 * упёршись в потолок, прореживание начинало грубеть, и длинный
 * рисунок, живой во время ведения, на отпускании превращался
 * в свою упрощённую тень. Замеров ради: извилистый штрих через
 * весь кадр — это около семисот точек, то есть тысяча оставляла
 * запас всего в треть.
 *
 * Четыре тысячи весят 68 КБ в JSON. Считать надо не по одному
 * штриху, а по СНАПШОТУ: сцена уезжает целиком каждому зрителю
 * при каждом переподключении, и в кадр протокола (1 МБ) таких
 * штрихов влезает пятнадцать. Восемь тысяч влезли бы всемером —
 * это уже похоже на настоящую сцену, и потому потолок здесь.
 */
export const MAX_POINTS = 4000;
const SVG_NS = 'http://www.w3.org/2000/svg';
export const shapeWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            kind: oneOf(TYPE, props, 'kind', KINDS, 'rect'),
            fill: color(TYPE, props, 'fill', '#000000'),
            stroke: color(TYPE, props, 'stroke', '#000000'),
            strokeWidth: numberInRange(TYPE, props, 'strokeWidth', 0, 1000, 0),
            radius: numberInRange(TYPE, props, 'radius', 0, 1000, 0),
            opacity: numberInRange(TYPE, props, 'opacity', 0, 1, 1),
            points: pointList(TYPE, props, 'points', MAX_POINTS),
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        const body = document.createElement('div');
        el.append(body);
        apply(el, body, props);
        return {
            el,
            update(next) {
                apply(el, body, next);
            },
            destroy() {
                el.remove();
            },
        };
    },
};
function apply(el, body, props) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. Требование
       иммутабельности относится к сцене, а не к узлам, которыми
       её рисуют. Тот же случай, что в apps/overlay/src/render.ts. */
    body.style.width = '100%';
    body.style.height = '100%';
    el.style.opacity = String(props.opacity);
    if (DRAWN.has(props.kind)) {
        drawn(body, props);
        /* eslint-enable no-param-reassign */
        return;
    }
    /* eslint-disable no-param-reassign -- см. выше */
    // Возврат к заливке после ломаной: тот же слой мог быть штрихом
    // до правки вида, и оставшийся внутри svg рисовался бы поверх
    body.replaceChildren();
    body.style.background = props.fill;
    body.style.boxSizing = 'border-box';
    // Ноль означает «без обводки», а не «обводка нулевой толщины»:
    // браузер рисует border: 0px solid как ничего, но свойство
    // остаётся в стиле и мешает читать вычисленные стили в тестах
    if (props.strokeWidth > 0) {
        body.style.border = `${props.strokeWidth}px solid ${props.stroke}`;
    }
    else {
        body.style.removeProperty('border');
    }
    // У эллипса скругление задаётся половиной размера и не зависит
    // от radius: смешивать их значило бы получить фигуру, которая
    // при одном radius эллипс, а при другом — что-то среднее
    body.style.borderRadius = props.kind === 'ellipse' ? '50%' : `${props.radius}px`;
    /* eslint-enable no-param-reassign */
}
/**
 * Ломаная внутри коробки слоя.
 *
 * ПОЧЕМУ preserveAspectRatio="none", ХОТЯ ЭТО И ЕСТЬ ИСКАЖЕНИЕ.
 * Коробка слоя заводится ровно по рамке нарисованного, так что
 * при рождении искажать нечего. Дальше её тянут руками — и тянут
 * затем, чтобы РАСТЯНУТЬ РИСУНОК: стрелка, у которой поехало
 * остриё, — это ровно то, что делают в любом редакторе, когда
 * тянут стрелку за угол. Сохранение пропорций дало бы обратное:
 * рисунок, отказывающийся заполнять свою же коробку.
 *
 * УЗЛЫ ПЕРЕИСПОЛЬЗУЮТСЯ, А НЕ СОЗДАЮТСЯ ЗАНОВО, и это не
 * микрооптимизация. Рисунок в работе обновляется до шестидесяти
 * раз в секунду, и каждый раз ломаная приходит ЦЕЛИКОМ — она
 * растёт вместе со штрихом. Пересборка svg на каждом обновлении
 * означала бы разбор тысячи точек и создание узлов шестьдесят раз
 * в секунду: на замере с придушенным вшестеро процессором кадр
 * оверлея раздувался с 17 до 111 мс, а картинка отставала от руки
 * тем сильнее, чем дольше рисуют. Снаружи это выглядело как
 * замедленная съёмка.
 */
function drawn(body, props) {
    const points = pairs(props.points);
    // Заливка и рамка убираются явно: тот же слой мог быть
    // прямоугольником до правки вида, и за ломаной остался бы
    // цветной прямоугольник
    body.style.removeProperty('background');
    body.style.removeProperty('box-sizing');
    body.style.removeProperty('border');
    body.style.removeProperty('border-radius');
    // Одна точка — это ещё не линия. Такое приходит от щелчка без
    // движения: рисовать нечего, но и падать не за что
    if (points.length < 2) {
        body.replaceChildren();
        return;
    }
    const svg = ensureSvg(body);
    const view = viewBox(points, props.strokeWidth);
    svg.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);
    const head = props.kind === 'arrow' ? arrowHead(points, props.strokeWidth) : null;
    const lines = ensureLines(svg, head === null ? 1 : 2);
    applyLine(lines[0], points, props);
    if (head !== null)
        applyLine(lines[1], head, props);
}
/** Холст ломаной. Создаётся один раз на слой. */
function ensureSvg(body) {
    const found = body.querySelector('svg');
    if (found !== null)
        return found;
    body.replaceChildren();
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    // Узел рисунка не должен перехватывать указатель: нажатия
    // по слою разбирает полотно панели, как и у всех остальных
    svg.style.display = 'block';
    svg.style.pointerEvents = 'none';
    body.append(svg);
    return svg;
}
/**
 * Ровно столько ломаных, сколько нужно виду.
 *
 * У стрелки их две — линия и остриё, — у прочих одна. Лишние
 * убираются: слой мог быть стрелкой до правки вида, и брошенное
 * остриё висело бы в воздухе.
 */
function ensureLines(svg, count) {
    const lines = Array.from(svg.querySelectorAll('polyline'));
    for (const extra of lines.slice(count))
        extra.remove();
    while (lines.length < count) {
        const line = document.createElementNS(SVG_NS, 'polyline');
        line.setAttribute('fill', 'none');
        // Скруглённые концы и стыки: у ломаной от руки острые углы
        // на каждом изломе выглядят зазубринами, а не линией
        line.setAttribute('stroke-linecap', 'round');
        line.setAttribute('stroke-linejoin', 'round');
        svg.append(line);
        lines.push(line);
    }
    return lines.slice(0, count);
}
/** Переносит точки и оформление в готовый узел. */
function applyLine(el, points, props) {
    el.setAttribute('points', points.map((p) => `${p.x},${p.y}`).join(' '));
    el.setAttribute('stroke', props.stroke);
    el.setAttribute('stroke-width', String(Math.max(props.strokeWidth, 1)));
}
/** Плоский список в точки. Нечётный хвост уже отсеян разбором. */
function pairs(flat) {
    const out = [];
    for (let i = 0; i + 1 < flat.length; i += 2) {
        out.push({ x: flat[i], y: flat[i + 1] });
    }
    return out;
}
/**
 * Рамка вокруг точек с запасом на толщину.
 *
 * Запас обязателен: линия рисуется ПО ЦЕНТРУ пути, и без него
 * половина толщины уходила бы за край viewBox — а именно край
 * и есть граница слоя. Толстая линия обрезалась бы вдоль.
 *
 * Нулевая сторона — не вырожденный случай, а обычный: у строго
 * горизонтальной линии высота рамки ровно ноль. Запас делает её
 * положительной сам.
 */
function viewBox(points, strokeWidth) {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const pad = Math.max(strokeWidth, 1) / 2;
    const minX = Math.min(...xs) - pad;
    const minY = Math.min(...ys) - pad;
    return {
        x: minX,
        y: minY,
        w: Math.max(...xs) + pad - minX,
        h: Math.max(...ys) + pad - minY,
    };
}
/** Насколько остриё длиннее толщины линии. */
const HEAD_SCALE = 4;
/** Половина раствора острия, в радианах (25°). */
const HEAD_SPREAD = (25 * Math.PI) / 180;
/**
 * Остриё стрелки — та же ломаная, а не залитый треугольник.
 *
 * Ломаной, потому что остриё обязано выглядеть продолжением линии:
 * у залитого треугольника своя геометрия, и при тонкой линии он
 * читается как отдельная фигура на её конце, а при толстой —
 * тонет в ней.
 *
 * Считается по ПОСЛЕДНЕМУ отрезку, а не по прямой от начала:
 * у стрелки их и так два конца, но нарисованная от руки дуга
 * должна получить остриё по касательной, а не по хорде.
 */
function arrowHead(points, strokeWidth) {
    const tip = points[points.length - 1];
    // Ищем ближайшую точку, отличную от острия: последние две
    // совпадают, если рисующий замер, и направление из них
    // не вычислить — вышло бы деление на ноль
    let from = null;
    for (let i = points.length - 2; i >= 0; i -= 1) {
        const candidate = points[i];
        if (candidate.x !== tip.x || candidate.y !== tip.y) {
            from = candidate;
            break;
        }
    }
    if (from === null)
        return null;
    const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
    const length = Math.max(strokeWidth, 1) * HEAD_SCALE;
    const wing = (turn) => ({
        x: tip.x - length * Math.cos(angle + turn),
        y: tip.y - length * Math.sin(angle + turn),
    });
    return [wing(-HEAD_SPREAD), tip, wing(HEAD_SPREAD)];
}
/**
 * Переводит нарисованное в слой: рамку и свойства фигуры.
 *
 * ЖИВЁТ ЗДЕСЬ, А НЕ В ПАНЕЛИ, потому что считает это дважды:
 * панель — когда собирает операцию, оверлей — когда рисует чужой
 * штрих, пока его ведут. Две реализации одной арифметики разошлись
 * бы молча, и разницу увидел бы зритель: рисунок в работе стоял бы
 * не там, где встанет готовый.
 *
 * ТОЧКИ ВОЗВРАЩАЮТСЯ ОТНОСИТЕЛЬНО РАМКИ, а не в единицах кадра.
 * Иначе свойства слоя зависели бы от того, где он лежит, и первый
 * же перенос слоя мышью сделал бы их ложью.
 *
 * Возвращает null, если рисовать нечего: точек меньше двух.
 */
export function sketchLayer(kind, points, color, strokeWidth) {
    if (points.length < 2)
        return null;
    const boxed = !DRAWN.has(kind);
    // У коробки рамку задают два угла — начало протяжки и её конец;
    // у ломаной рамкой служит её собственный размах
    const corners = boxed ? [points[0], points[points.length - 1]] : points;
    const pad = boxed ? 0 : Math.max(strokeWidth, 1) / 2;
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    const x = Math.min(...xs) - pad;
    const y = Math.min(...ys) - pad;
    const rect = {
        x,
        y,
        w: Math.max(...xs) + pad - x,
        h: Math.max(...ys) + pad - y,
    };
    return {
        rect,
        props: {
            kind,
            // Заливка у коробки, обводка у ломаной — но задаются оба:
            // цвет один, и менять его потом придётся в одном месте,
            // а не гадать, какое поле у этого вида главное
            fill: color,
            stroke: color,
            strokeWidth: boxed ? 0 : strokeWidth,
            radius: 0,
            opacity: 1,
            points: boxed ? [] : corners.flatMap((p) => [round(p.x - x), round(p.y - y)]),
        },
    };
}
/**
 * Округление до сотых.
 *
 * Точка приходит от мыши поделённой на масштаб, то есть с длинным
 * хвостом: без округления каждая координата уезжает в сеть как
 * «412.33333333333337», и штрих на тысячу точек раздувается вдвое
 * на одном только представлении чисел.
 */
function round(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=shape.js.map